import React from "react";

// Palette: meditative / cosmic colors — distinct from rangoli's festival palette.
export const MANDALA_PALETTE = [
  { name: "Gold", value: "#E0A82E" },
  { name: "Saffron", value: "#F49B0B" },
  { name: "Rose", value: "#E54B6A" },
  { name: "Crimson", value: "#C03546" },
  { name: "Violet", value: "#6A4FB6" },
  { name: "Amethyst", value: "#9B59B6" },
  { name: "Sapphire", value: "#1F76A8" },
  { name: "Teal", value: "#2DB7A1" },
  { name: "Pine", value: "#2E5E3E" },
  { name: "Cream", value: "#F9F6F0" },
  { name: "Bronze", value: "#7B3F1D" },
  { name: "Ink", value: "#0A0F22" },
];

// Backgrounds — darker palette suits meditative mandala feel.
export const MANDALA_BACKGROUNDS = [
  { name: "Midnight", value: "#0A0F22" },
  { name: "Indigo", value: "#1E2A4F" },
  { name: "Plum", value: "#3B1F4A" },
  { name: "Cream", value: "#F9F6F0" },
  { name: "Bronze", value: "#3B240F" },
];

export const SEGMENT_OPTIONS = [6, 8, 12, 16, 24, 32];

// ---------- Motifs ----------
// Each motif renders a single instance centered along the +X axis at distance r
// from origin. The ring code rotates N copies of this around the origin.

export const MANDALA_MOTIFS = [
  {
    id: "ring",
    name: "Ring",
    continuous: true, // a single circle, no segments
    render: ({ r, color, thickness }) => (
      <circle cx={0} cy={0} r={r} fill="none" stroke={color} strokeWidth={thickness} />
    ),
  },
  {
    id: "dot",
    name: "Dots",
    render: ({ r, color, thickness }) => (
      <circle cx={r} cy={0} r={thickness * 1.6} fill={color} />
    ),
  },
  {
    id: "petal",
    name: "Petals",
    render: ({ r, color, thickness }) => (
      <path
        d={`M ${r - 18} 0 Q ${r - 8} -${thickness * 2.4} ${r + 18} 0 Q ${r - 8} ${
          thickness * 2.4
        } ${r - 18} 0 Z`}
        fill={color}
      />
    ),
  },
  {
    id: "leaf",
    name: "Leaves",
    render: ({ r, color, thickness }) => (
      <path
        d={`M ${r - 14} 0 Q ${r - 2} -${thickness * 2.8} ${r + 22} 0 Q ${r - 2} ${
          thickness * 2.8
        } ${r - 14} 0 Z`}
        fill={color}
      />
    ),
  },
  {
    id: "diamond",
    name: "Diamonds",
    render: ({ r, color, thickness }) => (
      <polygon
        points={`${r - thickness * 1.6},0 ${r},${-thickness * 1.4} ${r + thickness * 1.6},0 ${r},${
          thickness * 1.4
        }`}
        fill={color}
      />
    ),
  },
  {
    id: "triangle-out",
    name: "Triangles ▶",
    render: ({ r, color, thickness }) => (
      <polygon
        points={`${r - thickness * 1.4},${-thickness * 1.4} ${r + thickness * 1.6},0 ${r - thickness * 1.4},${
          thickness * 1.4
        }`}
        fill={color}
      />
    ),
  },
  {
    id: "triangle-in",
    name: "Triangles ◀",
    render: ({ r, color, thickness }) => (
      <polygon
        points={`${r + thickness * 1.4},${-thickness * 1.4} ${r - thickness * 1.6},0 ${r + thickness * 1.4},${
          thickness * 1.4
        }`}
        fill={color}
      />
    ),
  },
  {
    id: "lotus",
    name: "Lotus tips",
    render: ({ r, color, thickness }) => (
      <path
        d={`M ${r} ${-thickness * 2.2} Q ${r + thickness * 1.6} 0 ${r} ${
          thickness * 2.2
        } Q ${r - thickness * 1.6} 0 ${r} ${-thickness * 2.2} Z`}
        fill={color}
      />
    ),
  },
  {
    id: "spoke",
    name: "Spokes",
    render: ({ r, color, thickness }) => (
      <line
        x1={r - thickness * 2.5}
        y1={0}
        x2={r + thickness * 2.5}
        y2={0}
        stroke={color}
        strokeWidth={thickness * 0.8}
        strokeLinecap="round"
      />
    ),
  },
  {
    id: "ring-outline",
    name: "Hollow ring",
    continuous: true,
    render: ({ r, color, thickness }) => (
      <g>
        <circle cx={0} cy={0} r={r + thickness * 0.6} fill="none" stroke={color} strokeWidth={1.5} />
        <circle cx={0} cy={0} r={r - thickness * 0.6} fill="none" stroke={color} strokeWidth={1.5} />
      </g>
    ),
  },
];

// ---------- Facts ----------
export const MANDALA_FACTS = [
  {
    title: "What is a Mandala?",
    body:
      "Mandala means 'circle' in Sanskrit. It's a beautiful pattern of circles within circles — used for centuries as a way to calm the mind and look closely at small things.",
    emoji: "🔵",
  },
  {
    title: "Where does it come from?",
    body:
      "Mandalas appear across many cultures — Indian Hindu and Buddhist traditions, Tibetan sand paintings, even Native American art. The idea of drawing in circles to focus the mind is very old and very human.",
    emoji: "🌏",
  },
  {
    title: "How do you draw one?",
    body:
      "Always from the centre outwards. Pick a small motif and repeat it in a ring. Then add another ring, and another. Watch the pattern grow like ripples in a pond.",
    emoji: "🪷",
  },
  {
    title: "Why are they calming?",
    body:
      "Because each ring has the same motif repeated in perfect symmetry — your eyes and your breath fall into the rhythm of it. Drawing a mandala is a way of being quiet on the inside.",
    emoji: "🧘🏽",
  },
];
